// ─── Where a company's revenue comes from, normalised ────────────────────────
//
// The SEC does not require a shared vocabulary for geography, and companies do
// not converge on one. Fifty filings produced FIFTY-FOUR distinct labels for
// what is really a handful of places: "US", "Americas", "North America" and
// "US Canada" are four ways of writing the same continent, and "Chinaincluding
// Hong Kong" is a fifth spelling of a third.
//
// Every one of those 54 labels is listed below by hand. Nothing is inferred at
// runtime — no substring matching, no fuzzy comparison, no "if it contains
// Europe". A label that is not in this file does not silently vanish and does
// not get guessed at: it lands in `other`, is reported as unrecognised, and is
// named on screen, so the gap is visible instead of quietly absorbed.

// The canonical axis. Five PLACES, chosen to be the coarsest split that still
// answers "where is my portfolio concentrated" for an Israeli investor — Israel
// is its own bucket for that reason, not because the filings separate it — plus
// three buckets that are not places at all. `place: false` marks those, because
// a screen must not say "no exposure to מחוץ לארה\"ב · לא פורט": that is a
// statement about what a filing disclosed, not about where money came from.
export const REGIONS = [
  { id: "north_america", he: "צפון אמריקה",      place: true },
  { id: "europe",        he: "אירופה",           place: true },
  { id: "asia_pacific",  he: "אסיה·פסיפיק",       place: true },
  { id: "israel",        he: "ישראל",             place: true },
  { id: "latam",         he: "אמריקה הלטינית",    place: true },
  // "Americas" is North AND South America together (Apple, among others).
  // Filed under צפון אמריקה it named a continent the filing did not; it is
  // its own bucket, called what the company called it (2026-09-26).
  { id: "americas",      he: "אמריקה",           place: true },

  // ─── The three buckets that are NOT places ─────────────────────────────────
  // These used to be one bucket called "אחר", which led the map at 39% and said
  // the wrong thing about itself: "אחר" reads as "we could not classify this",
  // and the classification is not what failed. Measured across all 46 companies
  // with a geographic breakdown, 96.5% of that bucket is revenue THE COMPANY
  // CHOSE NOT TO SPLIT. Two different choices, so two different labels:
  //
  //   intl_unspecified — the company divided the world in two and stopped.
  //                      Microsoft, Visa, Coca-Cola and Walmart file "Non US"
  //                      with no Europe line and no Asia line at all.
  //   rest_unspecified — the company did name regions, then swept up what was
  //                      left. Apple files US · China · "Other Countries" 48%.
  //
  // `other` survives for a third case that is neither: a real place with no
  // bucket of its own — Middle East, Africa, ZA. It is 0.22 of weight across
  // the whole universe (3.5% of what used to be "אחר") and appears for three
  // companies. It is kept separate rather than folded into either label above
  // because those companies DID break the region out, and filing it under
  // "לא פורט" would contradict their own statement.
  { id: "intl_unspecified", he: 'מחוץ לארה"ב · לא פורט', place: false },
  { id: "rest_unspecified", he: "שאר העולם · לא פורט",   place: false },
  { id: "other",            he: "אחר",                   place: false },
];

export const REGION_HE = Object.fromEntries(REGIONS.map(r => [r.id, r.he]));

// ── Leaf labels · each names exactly one region ──────────────────────────────
// A leaf is a place. Summing leaves is always safe: no leaf contains another.
const LEAF = {
  // North America
  "US": "north_america",
  "CA": "north_america",
  "MX": "north_america",
  "US Canada": "north_america",
  "North America": "north_america",

  // North and South America together
  "Americas": "americas",

  // Europe
  "EMEA": "europe",
  "Europe": "europe",
  "European Union": "europe",
  "GB": "europe",
  "DE": "europe",
  "FR": "europe",
  "ES": "europe",
  "IT": "europe",
  "RU": "europe",

  // Asia-Pacific
  "Asia Pacific": "asia_pacific",
  "Asia": "asia_pacific",
  "CN": "asia_pacific",
  "JP": "asia_pacific",
  "TW": "asia_pacific",
  "SG": "asia_pacific",
  "HK": "asia_pacific",
  "KR": "asia_pacific",
  "AU": "asia_pacific",
  "Oceania": "asia_pacific",
  "China Including Hong Kong": "asia_pacific",
  "Chinaincluding Hong Kong": "asia_pacific",
  "Rest Of Asia": "asia_pacific",
  // Apple names its reportable segments in words, on the business axis
  // (exposure.js reads them as a geographic split · see regionRows).
  "Greater China": "asia_pacific",
  "Japan": "asia_pacific",
  "Rest Of Asia Pacific": "asia_pacific",
  "Other Asia Pacific": "asia_pacific",
  "Asia Pacific Africa": "asia_pacific",

  // Israel
  "IL": "israel",

  // Latin America
  "Latin America": "latam",
  "BR": "latam",
  "Other Americas": "latam",
  "Americas Excluding U.S.": "latam",
  "Americas Excluding United States": "latam",
  "Western Hemisphere Excluding US": "latam",

  // Genuinely elsewhere
  "Africa": "other",
  "ZA": "other",
  "Middle East": "other",
  "Middle East And Africa": "other",
};

// ── Aggregate labels · each CONTAINS other regions ───────────────────────────
//
// This is the part that has to be declared rather than guessed. Thirty-four of
// the forty-six companies with a geographic breakdown report an aggregate
// ALONGSIDE its own components — "Non US" on the same statement as "EMEA" and
// "Asia Pacific" — because the filing shows both a roll-up line and its detail.
// Adding them up counts the same revenue twice.
//
// `covers` states which canonical regions each aggregate spans. The resolution
// rule below uses it; nothing reads the label text.
const AGGREGATE = {
  "Non Us":                    { covers: ["europe", "asia_pacific", "israel", "latam", "other"] },
  "Total International":       { covers: ["europe", "asia_pacific", "israel", "latam", "other"] },
  "International Markets":     { covers: ["europe", "asia_pacific", "israel", "latam", "other"] },
  "International Developed Markets": { covers: ["europe", "asia_pacific", "other"] },
  "International Emerging Markets":  { covers: ["asia_pacific", "latam", "other"] },
};

// ── Residual labels · "everything I did not name above" ──────────────────────
//
// A residual is disjoint from the named lines by construction — that is what
// makes it a residual — so unlike an aggregate it is ALWAYS safe to keep. It
// goes to `rest_unspecified`: the company named regions and then swept up the
// remainder, which is a different statement from dividing the world in two.
const RESIDUAL = new Set([
  "Other Countries",
  "Rest Of World",
  "All Other Countries",
  "All Other Geographies Member",
  "Other Foreign",
  "Other Country",
  "Other Areas",
  "Other",
]);

/** What kind of line is this, and where does it point? */
export function classifyRegionLabel(label) {
  const l = String(label || "").trim();
  if (LEAF[l])      return { kind: "leaf",      region: LEAF[l] };
  if (AGGREGATE[l]) return { kind: "aggregate", covers: AGGREGATE[l].covers };
  if (RESIDUAL.has(l)) return { kind: "residual", region: "rest_unspecified" };
  return { kind: "unknown", region: "other" };
}

// ─── The subtotal rule, stated once ──────────────────────────────────────────
/**
 * Resolve one company's geographic lines into non-overlapping regions.
 *
 * THE RULE: an aggregate is dropped when the company also reports at least one
 * of the regions it covers. The detail wins over the roll-up, because the
 * detail is what the axis is for and because keeping both counts the same
 * revenue twice.
 *
 * An aggregate that overlaps nothing is KEPT — it is then the only statement
 * the company makes about that part of the world, and dropping it would delete
 * real revenue. It goes to `intl_unspecified`, because "everything that is not
 * the United States" is not the same claim as "Europe".
 *
 * Nothing is ever distributed across regions. Splitting a "Non US" line into
 * Europe and Asia by any ratio would be inventing a number the company did not
 * report, which is the one thing this project does not do.
 *
 * @param rows [{ segment_name, revenue }] — one fiscal year, one company
 * @returns { byRegion, dropped, unknown, base }
 */
export function resolveCompanyRegions(rows) {
  const leaves = [];
  const aggregates = [];
  const residuals = [];
  const unknown = [];

  for (const r of rows) {
    if (r.revenue == null || !(r.revenue > 0)) continue;
    const c = classifyRegionLabel(r.segment_name);
    if (c.kind === "leaf")           leaves.push({ ...r, region: c.region });
    else if (c.kind === "aggregate") aggregates.push({ ...r, covers: c.covers });
    else if (c.kind === "residual")  residuals.push({ ...r, region: "rest_unspecified" });
    // An unrecognised label is a classification failure, which is what `other`
    // actually means — it is not the company declining to split.
    else { unknown.push(r.segment_name); residuals.push({ ...r, region: "other" }); }
  }

  const named = new Set(leaves.map(l => l.region));

  const kept = [...leaves, ...residuals];
  const dropped = [];
  for (const a of aggregates) {
    // A roll-up contains its components, so it cannot be SMALLER than them.
    // Amazon files US · Germany · UK · Japan · "Non Us" 107.5B, and 107.5 is
    // less than the 119.8 of Germany, UK and Japan: that line is the rest of
    // the world, not a subtotal. Dropping it deleted 15% of Amazon's revenue
    // and inflated every other share (found 2026-09-25). Measured, not read
    // from the label.
    // Only for a roll-up of the whole world outside the US ("Non Us", "Total
    // International"): for a partial one ("Emerging Markets") the covered
    // buckets are coarser than the roll-up, and the sum would not be its
    // components.
    const wholeWorld = a.covers.length >= 5;
    const coveredSum = leaves.filter(l => a.covers.includes(l.region)).reduce((s, l) => s + l.revenue, 0);
    if (wholeWorld && coveredSum > 0 && a.revenue < coveredSum) {
      kept.push({ ...a, region: "rest_unspecified", residualByArithmetic: true });
      continue;
    }
    if (a.covers.some(region => named.has(region))) {
      // Its components are already on the statement — this line is a roll-up
      // of lines we have counted.
      dropped.push({ label: a.segment_name, revenue: a.revenue, reason: "subtotal_of_reported_regions" });
    } else {
      kept.push({ ...a, region: "intl_unspecified", aggregate: true });
    }
  }

  const byRegion = {};
  for (const k of kept) byRegion[k.region] = (byRegion[k.region] || 0) + k.revenue;
  const base = Object.values(byRegion).reduce((a, b) => a + b, 0);

  return { byRegion, dropped, unknown: [...new Set(unknown)], base };
}

// ─── What a company means by its own region names ───────────────────────────
// A region is shown as the company defines it. Apple's "Europe" is not
// Europe: its 10-K says the segment includes India, the Middle East and
// Africa. Filed under the canonical bucket without saying so, the number
// would describe a place it does not describe.
//
// Keyed by ticker AND fiscal year, on purpose. The definition is a sentence
// from one annual report; when the next report arrives the key stops
// matching and the note disappears, rather than going stale in silence
// (CLAUDE.md). Adding a year is a deliberate act of reading that year's 10-K.
export const COMPANY_REGION_DEFS = {
  "AAPL|2025": {
    source: { form: "10-K", year: 2025 },
    labels: {
      "Americas":             { he: "אמריקה",           note: "צפון ודרום אמריקה" },
      "Europe":               { he: "אירופה",           note: "כולל הודו, המזרח התיכון ואפריקה" },
      "Greater China":        { he: "סין רבתי",         note: "סין, הונג קונג וטייוואן" },
      "Japan":                { he: "יפן" },
      "Rest Of Asia Pacific": { he: "שאר אסיה·פסיפיק",  note: "אוסטרליה ומדינות אסיה שאינן בסגמנטים האחרים" },
    },
  },
};

export function companyRegionDef(symbol, year) {
  return COMPANY_REGION_DEFS[`${symbol}|${year}`] || null;
}

// ─── A company's own region name, in Hebrew (sprint 13, 1.7) ────────────────
// A literal translation of the label the company filed, for display only ·
// "Americas" is אמריקה, "EMEA" is אירופה, המזרח התיכון ואפריקה. It says what
// the label says and no more: the grouping into canonical regions above is a
// separate step. A label not listed here is shown as filed.
const REGION_LABEL_HE = {
  "united states": 'ארה"ב',
  "us canada": 'ארה"ב וקנדה', "north america": "צפון אמריקה",
  "americas": "אמריקה",
  "emea": "אירופה, המזרח התיכון ואפריקה", "europe": "אירופה", "european union": "האיחוד האירופי",
  "asia pacific": "אסיה·פסיפיק", "asia": "אסיה", "oceania": "אוקיאניה",
  "china including hong kong": "סין, כולל הונג קונג", "chinaincluding hong kong": "סין, כולל הונג קונג",
  "rest of asia": "שאר אסיה", "greater china": "סין רבתי", "japan": "יפן",
  "rest of asia pacific": "שאר אסיה·פסיפיק", "other asia pacific": "אסיה·פסיפיק, אחר",
  "asia pacific africa": "אסיה·פסיפיק ואפריקה",
  "latin america": "אמריקה הלטינית", "other americas": "אמריקה, אחר",
  "americas excluding u.s.": 'אמריקה, בלי ארה"ב', "americas excluding united states": 'אמריקה, בלי ארה"ב',
  "western hemisphere excluding us": 'חצי הכדור המערבי, בלי ארה"ב',
  "africa": "אפריקה", "middle east": "המזרח התיכון",
  "middle east and africa": "המזרח התיכון ואפריקה",
  "non us": 'מחוץ לארה"ב', "international": "בינלאומי", "total international": 'בינלאומי, סה"כ',
  "international markets": "שווקים בינלאומיים",
  "international developed markets": "שווקים מפותחים, בינלאומי",
  "international emerging markets": "שווקים מתעוררים, בינלאומי",
  "other countries": "מדינות אחרות", "rest of world": "שאר העולם", "all other countries": "כל שאר המדינות",
  "all other geographies member": "כל שאר האזורים", "other foreign": "חוץ, אחר",
  "other country": "מדינה אחרת", "other areas": "אזורים אחרים",
};

// Two-letter country codes, as the geographic axis files them ("US", "CN").
// Only for that axis: on the business axis "IT" is a segment, not Italy.
const COUNTRY_CODE_HE = {
  US: 'ארה"ב', CA: "קנדה", MX: "מקסיקו", GB: "בריטניה", DE: "גרמניה", FR: "צרפת",
  ES: "ספרד", IT: "איטליה", RU: "רוסיה", CN: "סין", JP: "יפן", TW: "טייוואן",
  SG: "סינגפור", HK: "הונג קונג", KR: "דרום קוריאה", AU: "אוסטרליה", IL: "ישראל",
  BR: "ברזיל", ZA: "דרום אפריקה",
};

/**
 * The Hebrew for a filed region label, or null when it is not a region name.
 * `geographic`: the label comes from the geographic axis, where a two-letter
 * code is a country.
 */
export function regionLabelHe(label, { geographic = false } = {}) {
  const l = String(label || "").trim();
  if (geographic && COUNTRY_CODE_HE[l]) return COUNTRY_CODE_HE[l];
  return REGION_LABEL_HE[l.toLowerCase()] || null;
}
